import { keepPreviousData, QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Either } from '@playq/irt';
import { AppID, Environment, Filter, TimePrecision } from '@playq/octopus-common';
import { AttachmentSearchQuery } from '@playq/services-beetle';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import {
  IUseQueryOptionsExtended,
  useEitherQuery,
  useMutateQueryDataAfterEntityDeleting,
  useQueryPrefetch,
} from '/api/service-hooks';
import { toMs } from '/helpers';
import { appToolkit } from '/store';

import { calculateTotalAttachment } from '../helpers';

import { IBeetleRouteParams } from './types';
import { createMutateUpdated, createMutateUpdatedBatchStatus } from './cache';
import { getEnabledOrPrefetchStatus } from './helpers';
import { useDeleteCacheBatch } from './useDeleteCacheBatch';

/**
 * @template R - a successful response type.
 * @template ID - entity ID type.
 * @template E - entity type.
 * @template S - entity serialized.
 */
export const useAttachmentQueryBase = <
  R extends object,
  E extends { totalNotes: number },
  ID extends { serialize: () => string },
  S extends { id: string },
>(
  keys: QueryKey,
  initialQuery: {
    iterator: OffsetLimit;
    filterBy: {
      [key: string]: Filter;
    };
  },
  serviceMethod: (
    appID: AppID,
    query: AttachmentSearchQuery,
    iterator: OffsetLimit,
    filterBy: {
      [key: string]: Filter;
    },
    env: Environment
  ) => Promise<Either<GenericFailure, R | undefined>>,
  query: AttachmentSearchQuery,
  getID: (entity?: E) => ID | undefined,
  getEntities: (response: R) => E[],
  setEntities: (response: R, entities: E[]) => void,
  responseClass: (prevResponse: R) => R,
  getSerialize: (response: R) => S[],
  responseCs: (newArray: S[], updatedIterator: OffsetLimit, updatedTotal: number) => R,
  options?: IUseQueryOptionsExtended<R | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID) as AppID;
  const { env = Environment.Live } = useParams<IBeetleRouteParams>();
  const enabled = useMemo(() => getEnabledOrPrefetchStatus(options?.enabled, appID), [appID, options?.enabled]);
  const enablePrefetch = useMemo(
    () => getEnabledOrPrefetchStatus(options?.enablePrefetch, appID),
    [appID, options?.enablePrefetch]
  );
  const { iterator, filterBy } = initialQuery;

  const queryKeys: QueryKey = useMemo(
    () => [...keys, appID, query, iterator, filterBy, env],
    [keys, appID, query, iterator, filterBy, env]
  );
  const queryClient = useQueryClient();
  const { mutate, refetch, ...res } = useEitherQuery(
    queryKeys,
    () => serviceMethod(appID, query, iterator, filterBy, env),
    {
      placeholderData: keepPreviousData,
      staleTime: toMs(TimePrecision.Minute, 5),
      enabled,
      ...options,
    }
  );
  const entityAttachments = getEntities(res.data || ({} as R));
  const entityAttachmentsLength = entityAttachments.length ?? 0;

  const { nextKeys, nextIterator, preFetchData, setPreFetchData } = useQueryPrefetch({
    keys: queryKeys,
    enabled: enablePrefetch,
    total: entityAttachmentsLength,
    args: [appID, query, iterator, filterBy, env],
    serviceMethod,
    hasMore: entityAttachmentsLength === iterator.limit ? true : undefined,
  });
  const prefetchLength = getEntities(preFetchData || ({} as R)).length;
  const total = useMemo(
    () => calculateTotalAttachment(nextIterator, entityAttachmentsLength, iterator, prefetchLength ?? iterator.limit),
    [entityAttachmentsLength, iterator, nextIterator, prefetchLength]
  );
  useEffect(() => {
    function handlePreFetchData() {
      const isLimitReached = entityAttachmentsLength === nextIterator?.serialize().limit;

      if (isLimitReached) {
        if (nextKeys) {
          setPreFetchData(queryClient.getQueryData(nextKeys));
        }
      }
      return () => setPreFetchData(undefined);
    }
    handlePreFetchData();
    // nextKeys  makes event-loop in deps bacause we have query(calendar date) in cache that update every seconds
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityAttachmentsLength, queryClient, nextIterator, setPreFetchData]);

  const mutateDeletedEntity = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID,
    getEntities,
    setEntities,
    createNewResponse: (prevResponse) => responseClass(prevResponse),
  });

  const removeItemBatch = useDeleteCacheBatch({
    mutate,
    nextKeys,
    total,
    getSerialize,
    responseClass: responseCs,
    queryClient,
  });

  const mutateUpdated = createMutateUpdated(mutate);
  const mutateUpdatedBatchStatus = createMutateUpdatedBatchStatus(mutate);

  return {
    ...res,
    queryKeys,
    entities: entityAttachments,
    total,
    refetch,
    mutateDeletedEntity,
    mutateUpdated,
    mutateUpdatedBatchStatus,
    removeItemBatch,
  };
};
